import { Link } from "react-router-dom";
import { Flame, ShieldHalf, Sparkles, Crown, Swords, Map, Coins } from "lucide-react";
import { useGame } from "@/context/GameContext";
import { BuyLink } from "@/components/BuyLink";

const collections = [
  { k: "c1", icon: Flame, name: "Metaland Wolfpack Exclusive", count: "52 wolves", art: "/art/c1_exclusive.webp", boost: "+15% Attack", extra: "Unlocks Wolfpack Fury", ic: "text-fuchsia-300" },
  { k: "c2", icon: ShieldHalf, name: "Metaland Wolfpack", count: "777 wolves", art: "/art/sample_c2.webp", boost: "+20% Health & Defense", extra: "Unlocks Ironhide", ic: "text-cyan-300" },
  { k: "c3", icon: Sparkles, name: "Project PUPS", count: "3,333 pups", art: "/art/c3_pups.webp", boost: "+25% XP & Loot", extra: "1 free revive every day", ic: "text-violet-300" },
];

const steps = [
  { icon: Map, t: "Cross the forest", d: "Frostpine Forest is crawling with Shadowfang patrols. Pick your path stop by stop: fights, dens and shrines." },
  { icon: Swords, t: "Read the move", d: "Every enemy shows its next move. Guard the big hits, howl for power and strike when the guard drops." },
  { icon: Coins, t: "Play as your NFT", d: "Holders fight as the actual wolf or PUP they own, with permanent boosts. Anyone can buy credits for help." },
];

const NEON = [
  { x: 92.5, y: 44, w: 3, h: 9, c: "#ff3df2", d: "0s" },
  { x: 86.5, y: 52, w: 2.5, h: 6, c: "#ff4fd8", d: "1.3s" },
  { x: 94.5, y: 54, w: 4, h: 4, c: "#22d3ee", d: "2.1s" },
  { x: 64.5, y: 60, w: 2, h: 5, c: "#ff3df2", d: "0.7s" },
];

function HeroScene() {
  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="landing-hero-scene">
      <div className="hero-frame hero-sway">
        <img src="/art/hero_king.webp" alt="The crowned King Wolf in a moonlit forest" className="absolute inset-0 w-full h-full object-cover" fetchpriority="high" />
        {NEON.map((n, i) => (
          <span key={i} data-testid={`hero-neon-${i}`} className="neon-flicker absolute rounded-sm mix-blend-screen"
            style={{ left: `${n.x}%`, top: `${n.y}%`, width: `${n.w}%`, height: `${n.h}%`, background: `radial-gradient(closest-side, ${n.c}, transparent)`, animationDelay: n.d }} />
        ))}
        <span className="moon-pulse absolute rounded-full mix-blend-screen" style={{ left: "62%", top: "-2%", width: "26%", height: "38%" }} />
      </div>
      <div className="fog fog-a" />
      <div className="fog fog-b" />
    </div>
  );
}

export default function Landing() {
  const { user } = useGame();
  const cta = user ? "/journey" : "/login";
  return (
    <main className="relative">
      <section className="relative min-h-[92vh] flex items-end overflow-hidden grain" data-testid="landing-hero">
        <HeroScene />
        <div className="absolute inset-0 bg-gradient-to-t from-[#061016] via-[#061016]/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#061016]/95 via-[#061016]/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 pb-24 sm:pb-32">
          <p className="rise text-xs uppercase tracking-[0.4em] font-mono-game text-cyan-300/80 mb-6">Wolfpack Games · Most Wolf Player</p>
          <h1 className="rise font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-normal text-white max-w-3xl leading-[1.02] [font-kerning:normal] drop-shadow-[0_4px_16px_rgba(0,0,0,0.8)]" style={{ animationDelay: ".1s" }}>
            The Shadowfang hold the forest. <span className="italic text-amber-300">Take it back.</span>
          </h1>
          <p className="rise mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed" style={{ animationDelay: ".2s" }}>
            Wolves are held prisoner across 1,000 regions. Fight from Frostpine Forest to Moonfang Peak, free the pack and dethrone Nocturne, the Shadowfang King.
          </p>
          <div className="rise mt-10 flex flex-wrap gap-4" style={{ animationDelay: ".3s" }}>
            <Link to={cta} data-testid="landing-play-btn" className="ember-btn px-8 py-4 rounded-full text-sm">BEGIN THE HUNT</Link>
            <a href="#boosts" data-testid="landing-boosts-link" className="px-8 py-4 rounded-full text-sm border border-slate-600 text-slate-200 hover:border-amber-400 hover:text-amber-200 transition-colors">How NFT boosts work</a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-3 gap-10">
        {steps.map((s, i) => (
          <div key={s.t} className="rise" style={{ animationDelay: `${i * 0.1}s` }}>
            <s.icon className="w-7 h-7 text-fuchsia-400 mb-5" />
            <h3 className="font-display text-2xl text-white mb-3">{s.t}</h3>
            <p className="text-slate-400 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </section>

      <section id="boosts" className="relative py-24 border-y border-slate-800/70 bg-[#081820] noir-vignette">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] font-mono-game text-amber-400/90 mb-4">Three collections · Polygon</p>
            <h2 className="font-display text-4xl sm:text-5xl text-white mb-6">Your NFTs, <span className="italic text-amber-300">your edge.</span></h2>
            <p className="text-slate-400 leading-relaxed">Connect your wallet and sign a free message. No gas, no transaction. The game finds every wolf and PUP you hold, lets you play as one, and switches on each collection's boost.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {collections.map((c, i) => (
              <div key={c.name} data-testid={`landing-collection-${i + 1}`} className="group rounded-2xl overflow-hidden border border-slate-700/60 bg-[#061016] hover:-translate-y-1 hover:border-amber-400/50 transition-[transform,border-color] duration-300">
                <div className="relative aspect-square overflow-hidden">
                  <img src={c.art} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-black/60 text-[11px] font-mono-game text-slate-200">{c.count}</span>
                </div>
                <div className="p-6">
                  <p className="text-[11px] font-mono-game text-slate-500 mb-1">COLLECTION {i + 1}</p>
                  <h3 className="font-display text-xl text-white leading-snug mb-4 flex items-center gap-2"><c.icon className={`w-5 h-5 ${c.ic}`} />{c.name}</h3>
                  <p className={`font-semibold ${c.ic}`}>{c.boost}</p>
                  <div className="flex items-end justify-between gap-3 mt-1">
                    <p className="text-sm text-slate-400">{c.extra}</p>
                    <BuyLink k={c.k} testid={`landing-buy-${c.k}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-center gap-4 p-5 rounded-2xl border border-amber-500/30 bg-amber-500/5 max-w-2xl">
            <img src="/art/alpha.webp" alt="Pack Alpha" loading="lazy" className="w-20 h-20 rounded-xl object-cover" />
            <div>
              <p className="flex items-center gap-2 font-display text-lg text-amber-200"><Crown className="w-4 h-4" />Hold all three: Pack Bonus</p>
              <p className="text-sm text-slate-400 mt-1">+5% to every stat and the Pack Alpha title.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
        <img src="/art/boss.webp" alt="Grimfang, Shadowfang Warlord" loading="lazy" className="w-full max-w-md rounded-2xl fade-mask" />
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-mono-game text-red-400/80 mb-4">The villain</p>
          <h2 className="font-display text-4xl sm:text-5xl text-white mb-6">Grimfang waits in the fortress.</h2>
          <p className="text-slate-400 leading-relaxed mb-4">The Shadowfang warlord doesn't fall to button-mashing. He changes phase, warns of his worst blows and punishes greed. Fall, and you crawl back to the last den, unless you pay to rise again.</p>
          <p className="text-slate-500 text-sm mb-8">He is only the first. A named Shadowfang boss guards every 10th region, and the King waits at region 1,000.</p>
          <Link to={cta} data-testid="landing-bottom-play-btn" className="ember-btn inline-block px-8 py-4 rounded-full text-sm">PLAY FREE</Link>
        </div>
      </section>
      <div className="gold-line max-w-7xl mx-auto" />
      <footer className="py-8 text-center text-xs text-slate-600 font-mono-game">WOLFPACK GAMES · MOST WOLF PLAYER · A Metaland Wolfpack game · Credits are in-game only and cannot be cashed out.</footer>
    </main>
  );
}
